import React from 'react';
import { useNotes } from '../../context/NotesContext';
import useScreenSize from '../../hooks/useScreenSize';
import '../../styles/glass-morphism.css';

function NoteList() {
  const { notes, selectedNote, selectNote, createNote, isLoading } = useNotes();
  const { isMobile } = useScreenSize();

  const handleCreateNote = async () => {
    const result = await createNote({
      title: 'New Note',
      content: 'Start writing...',
    });

    if (result.success) {
      selectNote(result.note);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Notes</h3>
        <button
          type="button"
          onClick={handleCreateNote}
          className="internal-primary-action"
          style={{
            ...styles.addButton,
            padding: isMobile ? '10px 16px' : '8px 16px',
            minHeight: '44px',
          }}
          title="New Note"
        >
          + New Note
        </button>
      </div>

      {isLoading ? (
        <div style={styles.loading}>Loading...</div>
      ) : notes.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyText}>No notes yet</p>
          <p style={styles.emptySubtext}>Click "New Note" to create one</p>
        </div>
      ) : (
        <div style={styles.notesList}>
          {notes.map((note) => (
            <div
              key={note.id}
              onClick={() => selectNote(note)}
              style={{
                ...styles.noteItem,
                padding: isMobile ? '12px' : '16px',
                minHeight: isMobile ? '48px' : 'auto',
                ...(selectedNote && selectedNote.id === note.id ? styles.noteItemActive : {}),
              }}
              className="glass-hover smooth-transition"
            >
              <div style={styles.noteHeader}>
                <h4 style={styles.noteTitle}>{note.title}</h4>
                {note.is_favorite && (
                  <span style={styles.favoriteIcon}>★</span>
                )}
              </div>
              <p style={styles.noteSnippet}>{note.snippet || 'No content'}</p>
              <div style={styles.noteFooter}>
                <span style={styles.noteDate}>{formatDate(note.updated_at)}</span>
                {note.category && (
                  <span style={styles.categoryBadge}>
                    <span
                      style={{
                        ...styles.categoryDot,
                        background: note.category.color,
                      }}
                    />
                    {note.category.name}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'white',
    borderLeft: '1px solid #e1edf3',
    borderRight: '1px solid #e1edf3',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #e1edf3',
  },
  title: {
    color: '#102a43',
    fontSize: '1.2rem',
    fontWeight: '600',
    margin: 0,
  },
  addButton: {
    background: '#0778ad',
    border: '1px solid #0778ad',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loading: {
    padding: '32px',
    textAlign: 'center',
    color: '#52677c',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
  },
  emptyText: {
    color: '#334e68',
    fontSize: '1.1rem',
    marginBottom: '8px',
  },
  emptySubtext: {
    color: '#627d98',
    fontSize: '0.9rem',
  },
  notesList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
  },
  noteItem: {
    padding: '16px',
    marginBottom: '8px',
    borderRadius: '8px',
    cursor: 'pointer',
    background: '#f8fcfe',
    border: '1px solid #e1edf3',
  },
  noteItemActive: {
    background: '#e4f6fd',
    border: '1px solid #79cfe9',
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  noteTitle: {
    color: '#102a43',
    fontSize: '1rem',
    fontWeight: '600',
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  favoriteIcon: {
    color: '#ffc107',
    fontSize: '18px',
    marginLeft: '8px',
  },
  noteSnippet: {
    color: '#52677c',
    fontSize: '0.9rem',
    margin: '0 0 12px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.4',
  },
  noteFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noteDate: {
    color: '#829ab1',
    fontSize: '0.8rem',
  },
  categoryBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: '#edf6fa',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    color: '#334e68',
  },
  categoryDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
};

export default NoteList;
